import {
  getSupplementKnowledgeDetail,
  SupplementalKnowledgeBindItem,
  SupplementKnowledgeAnswer,
} from 'services/api/supplementKnowledge';
import type {
  CiteKnowledge,
  SaveKnowledge,
  SourceKnowledge,
  SupplementKnowledgeDetailResponse,
} from 'services/api/supplementKnowledge';
import { useTranslate } from '@tolgee/react';
import ConfirmModal, { DeleteConfirmModal, DeleteConfirmProps } from './ConfirmModal';
import ItemKnowledgeDrawer from './ItemKnowledgeDrawer';
import PageKnowledgeDrawer from './PageKnowledgeDrawer';
import useModal from 'hooks/useModal';
import { message, ModalProps } from 'antd';
import { useEffect, useMemo, useRef, useState } from 'react';
import { isEqual, uniqBy } from 'lodash-es';
import AssisKnowledgeDrawer from './AssisKnowledgeDrawer';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';
import { LoadingOutlined } from '@ant-design/icons';
import { overseaTranslate } from 'services/api/overseaTranslate';
import { sys2LangMap } from '../utils';
import { unstable_batchedUpdates } from 'react-dom';
import { useTriggerMapValue } from 'hooks/business/useTrigger';
import KnowledgeComponent from './Knowledge';
import { Container } from './styles';

interface Knowledge {
  citeKnowledge: CiteKnowledge;
  sourceKnowledge: SourceKnowledge;
}

type IKnowledgeList = Knowledge[];

interface KnowledgeListProps {
  caseId?: number;
  conversation: Conversation.Conversation;
  knowledgeList: IKnowledgeList;
  applySkuIds: string[];
  onKnowledgesUpdate?: (knowledegs: SaveKnowledge[][]) => void;
}

const isSameOriginKnowledge = (knowledge1: Knowledge, knowledge2: Knowledge) =>
  knowledge1.citeKnowledge.knowledgeId === knowledge2.citeKnowledge.knowledgeId ||
  (knowledge1.sourceKnowledge.kvId &&
    knowledge2.sourceKnowledge.kvId &&
    knowledge1.sourceKnowledge.kvId === knowledge2.sourceKnowledge.kvId &&
    knowledge2.citeKnowledge.bindItem.every((item) => {
      return knowledge1.citeKnowledge.bindItem.some(
        ({ spuId, skus }) =>
          spuId === item.spuId &&
          item.skus.every(({ skuId }) => skus.some((_) => _.skuId === skuId)),
      );
    }));

export const KnowledgeList = (props: KnowledgeListProps) => {
  const { caseId, conversation, knowledgeList, applySkuIds, onKnowledgesUpdate } = props;
  const { t } = useTranslate();
  const lang = useSystemLanguageValue();
  const { EDIT_CITE_KNOWLEDGE } = useTriggerMapValue();

  const confirmModalState = useModal<ModalProps>();
  const deleteConfirmModalState = useModal<DeleteConfirmProps>();
  const assisKnowledgeDrawerState = useModal<{
    knowledgeId: number;
    knowledge: Pick<SaveKnowledge, 'value' | 'bindItem'>;
    sidebarDataSource: SupplementKnowledgeDetailResponse;
    onKnowledgeChange: (knowledge: Pick<SaveKnowledge, 'value' | 'bindItem'>) => void;
  }>();
  const itemKnowledgeDrawerState = useModal<{
    kvId: number;
    knowledgeId: number;
    pointId: string;
    knowledge: CiteKnowledge;
    onKnowledgesChange: (knowledges: SaveKnowledge[]) => void;
  }>();
  const pageKnowledgeDrawerState = useModal<{
    kvId: number;
    pointId: string;
    knowledge: SourceKnowledge;
    updatedKnowledges?: SaveKnowledge[];
    onKnowledgesChange: (knowledges: SaveKnowledge[]) => void;
  }>();

  const [originKnowledgeList, setOriginKnowledgeList] = useState(knowledgeList);
  const [updatedKnowledges, setUpdatedKnowledges] = useState<(SaveKnowledge[] | undefined)[]>([]);
  const [originUpdatedKnowledges, setOriginUpdatedKnowledges] = useState<
    (SaveKnowledge[] | undefined)[]
  >([]);
  const [translatedMap, setTranslatedMap] = useState<Partial<Record<string, string>>>();

  const knowledgeListRef = useRef(knowledgeList);

  const originKnowledgeIndexList = useMemo(
    () =>
      knowledgeList.map((knowledge) => {
        return originKnowledgeList.findIndex((originKnowledge) =>
          isSameOriginKnowledge(originKnowledge, knowledge),
        );
      }),
    [knowledgeList, originKnowledgeList],
  );
  const changedList = useMemo(
    () =>
      knowledgeList.map((knowledge, index) => {
        const { citeKnowledge } = knowledge;
        const originKnowledge = originKnowledgeList[originKnowledgeIndexList[index]];
        if (!originKnowledge || isEqual(knowledge, originKnowledge)) {
          return false;
        }
        const transAnswer = (answer: SupplementKnowledgeAnswer) => {
          if (answer.msgType === 'TEXT') {
            return answer.message;
          }
          return answer.url;
        };
        const transBindItem = (bindItem: SupplementalKnowledgeBindItem) => {
          return {
            spuId: bindItem.spuId,
            skuIds: bindItem.skus.map(({ skuId }) => skuId),
          };
        };
        const cite = {
          key: citeKnowledge.key,
          value: citeKnowledge.value.map(transAnswer),
          bindItem: citeKnowledge.bindItem.map(transBindItem),
        };
        const source = {
          key: originKnowledge.sourceKnowledge.key,
          value: originKnowledge.sourceKnowledge.value.map(transAnswer),
          bindItem: originKnowledge.sourceKnowledge.bindItem.map(transBindItem),
        };
        return !isEqual(cite, source);
      }),
    [knowledgeList, originKnowledgeList, originKnowledgeIndexList],
  );

  useEffect(() => {
    if (isEqual(knowledgeList, knowledgeListRef.current)) {
      return;
    }
    setOriginKnowledgeList((cur) => {
      return knowledgeList.reduce(
        (res, cur) => {
          if (res.every((knowledge) => !isSameOriginKnowledge(cur, knowledge))) {
            res.push(cur);
          }
          return res;
        },
        [...cur],
      );
    });
    setUpdatedKnowledges([]);
  }, [knowledgeList]);

  useEffect(() => {
    setTranslatedMap((pre) => (Object.keys(pre || {}).length ? {} : pre));
  }, [lang]);
  useEffect(() => {
    const needTranslateList = knowledgeList.reduce(
      (res, { citeKnowledge, sourceKnowledge }) => {
        const { key, value } = citeKnowledge;
        const { key: sourceKey, value: sourceValue } = sourceKnowledge;
        if (!translatedMap?.[key] && key?.trim() && !res.includes(key)) {
          res.push(key);
        }
        if (!translatedMap?.[sourceKey] && sourceKey?.trim() && !res.includes(sourceKey)) {
          res.push(sourceKey);
        }
        return [...value, ...sourceValue].reduce((res, { msgType, message }) => {
          if (
            msgType === 'TEXT' &&
            message?.trim() &&
            !translatedMap?.[message] &&
            !res.includes(message)
          ) {
            res.push(message);
          }
          return res;
        }, res);
      },
      updatedKnowledges.reduce<string[]>((res, updatedKnowledge) => {
        return updatedKnowledge
          ? updatedKnowledge.reduce((res, { key, value }) => {
              if (!translatedMap?.[key] && key?.trim() && !res.includes(key)) {
                res.push(key);
              }
              return value.reduce((res, { msgType, message }) => {
                if (
                  msgType === 'TEXT' &&
                  message?.trim() &&
                  !translatedMap?.[message] &&
                  !res.includes(message)
                ) {
                  res.push(message);
                }
                return res;
              }, res);
            }, res)
          : res;
      }, []),
    );
    if (needTranslateList.length) {
      overseaTranslate(needTranslateList, lang, 'supplementKnowledge')
        .then((res) => {
          setTranslatedMap((pre) => {
            const next = res.reduce(
              (res, translated, index) => {
                res[needTranslateList[index]] = translated;
                return res;
              },
              { ...pre },
            );
            return next;
          });
        })
        .catch(() => {
          setTranslatedMap({});
        });
    } else {
      setTranslatedMap((map) => map || {});
    }
  }, [lang, translatedMap, knowledgeList, updatedKnowledges]);

  if (!translatedMap) {
    return (
      <Container>
        <h3>
          {t('引用到的知识')}（{knowledgeList.length}）
        </h3>
        <LoadingOutlined />
      </Container>
    );
  }

  const confirmModal = (
    <ConfirmModal
      visible={confirmModalState.visible}
      onCancel={() => confirmModalState.hide()}
      {...confirmModalState.meta}
    />
  );

  const deleteConfirmModal = deleteConfirmModalState.meta && (
    <DeleteConfirmModal
      visible={deleteConfirmModalState.visible}
      onCancel={() => deleteConfirmModalState.hide()}
      {...deleteConfirmModalState.meta}
    />
  );

  const assisKnowledgeDrawer = assisKnowledgeDrawerState.meta && (
    <AssisKnowledgeDrawer
      key={assisKnowledgeDrawerState.meta.knowledgeId}
      visible={assisKnowledgeDrawerState.visible}
      onClose={() => assisKnowledgeDrawerState.hide()}
      knowledge={assisKnowledgeDrawerState.meta.knowledge}
      sidebarDataSource={assisKnowledgeDrawerState.meta.sidebarDataSource}
      onKnowledgeChange={assisKnowledgeDrawerState.meta.onKnowledgeChange}
    />
  );

  const itemKnowledgeDrawer = itemKnowledgeDrawerState.meta && (
    <ItemKnowledgeDrawer
      key={itemKnowledgeDrawerState.meta.kvId}
      visible={itemKnowledgeDrawerState.visible}
      onClose={() => itemKnowledgeDrawerState.hide()}
      {...itemKnowledgeDrawerState.meta}
    />
  );

  const pageKnowledgeDrawer = pageKnowledgeDrawerState.meta && (
    <PageKnowledgeDrawer
      key={pageKnowledgeDrawerState.meta.knowledge.knowledgeId}
      conversation={conversation}
      translatedMap={translatedMap}
      visible={pageKnowledgeDrawerState.visible}
      onClose={() => pageKnowledgeDrawerState.hide()}
      {...pageKnowledgeDrawerState.meta}
    />
  );

  return (
    <>
      <div>
        {knowledgeList.some(
          ({ sourceKnowledge }) => sourceKnowledge.modify || sourceKnowledge.deleted,
        )
          ? t('该回复基于更新前的知识生成。点击【测试】，即可将答案同步至最新知识状态。')
          : t('若您发现下方的知识错误或过期，请修改相应的知识内容')}
      </div>
      {knowledgeList.map((knowledge, index) => {
        const { citeKnowledge, sourceKnowledge } = knowledge;

        const originKnowledgeIndex = originKnowledgeIndexList[index];
        const originKnowledge = originKnowledgeList[originKnowledgeIndex];

        const { knowledgeId = 0, key, value, source, bindItem } = citeKnowledge;
        const { kvId, pointId } = sourceKnowledge;

        const updatedKnowledge = updatedKnowledges[index];

        const onKnowledgesChange =
          onKnowledgesUpdate &&
          ((knowledges: SaveKnowledge[]) => {
            if (source !== 'AI运营_商品知识_商品属性') {
              const next = [...updatedKnowledges];
              next[index] = knowledges;
              setUpdatedKnowledges(next);
            } else {
              const knowledgesIndexMap = knowledges.reduce<Record<number, SaveKnowledge[]>>(
                (res, knowledge) => {
                  const targetIndex = knowledgeList.findIndex(
                    ({ sourceKnowledge }) =>
                      sourceKnowledge.source === 'AI运营_商品知识_商品属性' &&
                      sourceKnowledge.kvId === knowledge.kvId &&
                      `${sourceKnowledge.knowledgeId}` === `${knowledge.knowledgeId}`,
                  );
                  if (targetIndex === index || targetIndex < 0) {
                    if (res[index]) {
                      res[index].push(knowledge);
                    } else {
                      res[index] = [knowledge];
                    }
                  } else {
                    res[targetIndex] = [knowledge];
                  }
                  return res;
                },
                {},
              );
              const next = updatedKnowledges.map((knowledges, index) => {
                if (knowledgesIndexMap[index]) {
                  return knowledgesIndexMap[index];
                }
                if (
                  knowledges?.some(
                    (_) => _.source === 'AI运营_商品知识_商品属性' && _.kvId === kvId,
                  )
                ) {
                  return undefined;
                }
                return knowledges;
              });
              Object.entries(knowledgesIndexMap).forEach(([index, knowledges]) => {
                next[Number(index)] = knowledges;
              });
              setUpdatedKnowledges(next);
            }

            if (originKnowledgeIndex < 0) {
              return;
            }

            const nextKnowledges = (originUpdatedKnowledges[originKnowledgeIndex] || []).reduce(
              (res, knowledge) => {
                const existItem = res
                  .map(({ bindItem }) => bindItem)
                  .flat()
                  .reduce((res, item) => {
                    const targetIndex = res.findIndex(({ spuId }) => spuId === item.spuId);
                    if (targetIndex < 0) {
                      res.push(item);
                      return res;
                    }
                    res[targetIndex] = {
                      ...res[targetIndex],
                      skus: uniqBy([...res[targetIndex].skus, ...item.skus], 'skuId'),
                    };
                    return res;
                  }, [] as typeof knowledge.bindItem);

                const bindItem = knowledge.bindItem.reduce((res, item) => {
                  const target = existItem.find(({ spuId }) => spuId === item.spuId);
                  if (!target) {
                    res.push(item);
                    return res;
                  }
                  const skus = item.skus.filter((_) =>
                    target.skus.every(({ skuId }) => skuId !== _.skuId),
                  );
                  if (!skus.length) {
                    return res;
                  }
                  res.push({
                    ...item,
                    skus,
                  });
                  return res;
                }, [] as typeof knowledge.bindItem);

                if (!bindItem.length) {
                  return res;
                }
                res.push({
                  ...knowledge,
                  bindItem,
                });
                return res;
              },
              [...knowledges],
            );
            const nextOrigin = originUpdatedKnowledges.map((knowledges, index) => {
              if (index === originKnowledgeIndex) {
                return nextKnowledges;
              } else if (
                source === 'AI运营_商品知识_商品属性' &&
                knowledges?.some((_) => _.source === 'AI运营_商品知识_商品属性' && _.kvId === kvId)
              ) {
                return undefined;
              } else {
                return knowledges;
              }
            });
            nextOrigin[originKnowledgeIndex] = nextKnowledges;
            setOriginUpdatedKnowledges(nextOrigin);
            onKnowledgesUpdate(
              nextOrigin.map((_) =>
                _
                  ? _.map(({ knowledgeId, ...others }) => {
                      return {
                        ...others,
                        knowledgeId: (knowledgeId as any)?.toString()?.startsWith('test')
                          ? undefined
                          : knowledgeId,
                      };
                    })
                  : [],
              ),
            );
          });

        const edit =
          onKnowledgesChange &&
          (() => {
            if (source === '客服提交答案' || source === '主管提交历史未回复问题答案') {
              getSupplementKnowledgeDetail(knowledgeId)
                .then((sidebarDataSource) => {
                  const value = updatedKnowledge?.[0]?.value || sourceKnowledge.value;
                  const bindItem = updatedKnowledge?.[0]?.bindItem || sourceKnowledge.bindItem;
                  return value
                    .reduce((res, answer, index) => {
                      const { msgType, message, translateMap } = answer;
                      const langKey = sys2LangMap[lang.toUpperCase()];
                      if (msgType === 'TEXT' && message) {
                        return res.then((res) => {
                          return (
                            translatedMap?.[message]
                              ? Promise.resolve(translatedMap[message])
                              : overseaTranslate(message, lang, 'supplementKnowledge')
                          ).then((translated) => {
                            res[index] = {
                              ...answer,
                              translateMap: {
                                ...translateMap,
                                [langKey]: translated,
                              },
                            };
                            return res;
                          });
                        });
                      }
                      return res;
                    }, Promise.resolve([...value]))
                    .then((value) => {
                      unstable_batchedUpdates(() => {
                        assisKnowledgeDrawerState.show({
                          knowledgeId,
                          knowledge: {
                            value,
                            bindItem,
                          },
                          sidebarDataSource: {
                            ...sidebarDataSource,
                            supplementalKnowledgeValue: {
                              ...sidebarDataSource.supplementalKnowledgeValue,
                              answers: value,
                              originAnswers: value,
                            },
                          },
                          onKnowledgeChange: ({ value, bindItem }) =>
                            onKnowledgesChange([
                              {
                                knowledgeId,
                                key,
                                value,
                                source,
                                bindItem,
                                operate: 'UPDATE',
                                kvId,
                                pointId,
                              },
                            ]),
                        });
                      });
                    });
                })
                .catch(() => {
                  message.error(t('phrase.获取数据失败，请稍后再试'));
                });
            }
            if (source === 'AI运营_商品知识_商品属性' && kvId && originKnowledge?.citeKnowledge) {
              itemKnowledgeDrawerState.show({
                kvId,
                knowledgeId,
                pointId,
                knowledge: originKnowledge.citeKnowledge,
                onKnowledgesChange,
              });
            }
            if ((source === '商品详情页' || source === '商品详情页_人工修改') && kvId) {
              pageKnowledgeDrawerState.show({
                kvId,
                pointId,
                knowledge: sourceKnowledge,
                updatedKnowledges: updatedKnowledge,
                onKnowledgesChange,
              });
            }
          });

        const onEdit =
          edit &&
          (() => {
            if (EDIT_CITE_KNOWLEDGE) {
              edit();
            } else {
              confirmModalState.show({
                onOk: edit,
              });
            }
          });

        return (
          <KnowledgeComponent
            key={knowledgeId}
            changed={changedList[index]}
            index={index}
            caseId={caseId}
            knowledge={citeKnowledge}
            sourceKnowledge={sourceKnowledge}
            updatedKnowledges={updatedKnowledge}
            applySkuIds={applySkuIds}
            translatedMap={translatedMap}
            onEdit={onEdit}
            onDelete={
              onKnowledgesChange &&
              onEdit &&
              (() => {
                const onDelete = (operate: 'DELETE' | 'DISABLE') => {
                  onKnowledgesChange([
                    {
                      knowledgeId,
                      key,
                      value,
                      source,
                      bindItem,
                      operate,
                      kvId,
                      pointId,
                    },
                  ]);
                };
                deleteConfirmModalState.show({
                  mode: source === '客服提交答案' ? 'stop' : 'delete',
                  onEdit,
                  onDelete: () => onDelete('DELETE'),
                  onDeleteOnlyCurrent: () => onDelete('DISABLE'),
                });
              })
            }
          />
        );
      })}
      {confirmModal}
      {deleteConfirmModal}
      {assisKnowledgeDrawer}
      {itemKnowledgeDrawer}
      {pageKnowledgeDrawer}
    </>
  );
};
